import React from "react";
import LegalLayout from "../components/LegalLayout";

const P = ({ children }) => (
  <p className="mb-4 text-slate-700">{children}</p>
);
const UL = ({ children }) => (
  <ul className="mb-4 list-disc space-y-1.5 pl-6 text-slate-700">{children}</ul>
);

const sections = [
  {
    id: "intro",
    heading: "Introduction",
    content: (
      <P>
        These Terms of Service ("Agreement") govern your access to and use of
        BuyerBeacon Pro's data, identity, and marketing platform ("Services").
        By signing up, accessing, or using the Services you ("Company," "you")
        agree to be bound by this Agreement.
      </P>
    ),
  },
  {
    id: "definitions",
    heading: "1. Definitions",
    content: (
      <UL>
        <li>
          <strong>"Authorized Applications"</strong> — the permitted uses of
          Available Data, as set forth in the Data Catalog or applicable
          amendment.
        </li>
        <li>
          <strong>"Available Data"</strong> — the aggregate collection of
          segments and information contained in the Data Catalog.
        </li>
        <li>
          <strong>"Available Segments"</strong> — specific data elements tied
          to BuyerBeacon Pro cookies, tags, mobile/device IDs, hashed email
          matches, or other user designations.
        </li>
        <li>
          <strong>"Company Data"</strong> — data you transmit to the Platform
          via our pixel/API for use of the Services.
        </li>
        <li>
          <strong>"Confidential Information"</strong> — non-public information
          disclosed by one party to the other, marked or reasonably understood
          as confidential.
        </li>
        <li>
          <strong>"Data Catalog"</strong> — the collection of Available Data
          and our proprietary ID graph.
        </li>
        <li>
          <strong>"Platform"</strong> — BuyerBeacon Pro's proprietary platform.
        </li>
        <li>
          <strong>"Services"</strong> — the data and identity services set
          forth in a Service Order.
        </li>
      </UL>
    ),
  },
  {
    id: "license",
    heading: "2. License",
    content: (
      <P>
        BuyerBeacon Pro grants Company a worldwide, non-exclusive,
        non-transferable license to access and use the Platform and Available
        Data solely for the designated Authorized Applications during the Term,
        subject to Company's compliance with this Agreement. No title or
        ownership of the Available Data is transferred.
      </P>
    ),
  },
  {
    id: "company-data",
    heading: "3. Company Data",
    content: (
      <P>
        You grant BuyerBeacon Pro a limited, non-exclusive, non-sublicensable
        license to receive, store, reproduce, and transmit Company Data solely
        to provide the Services. We will process Company Data as a processor or
        sub-processor in compliance with applicable privacy laws.
      </P>
    ),
  },
  {
    id: "term-termination",
    heading: "4. Term & Termination",
    content: (
      <UL>
        <li>
          <strong>Term.</strong> This Agreement begins on the Effective Date
          and continues for twelve (12) months unless otherwise terminated.
        </li>
        <li>
          <strong>Termination for Cause.</strong> Either party may terminate
          upon ninety (90) days' written notice if the other party materially
          breaches and fails to cure. Non-curable breaches may result in
          immediate termination.
        </li>
        <li>
          <strong>Change in Law.</strong> If any change in applicable law makes
          provision of the Services unlawful, we may terminate immediately upon
          the effective date of the change.
        </li>
        <li>
          <strong>Effect of Termination.</strong> Upon termination, each party
          shall destroy the other's Confidential Information (and certify
          destruction within thirty (30) days), except one copy retained for
          legal/archival purposes. Company shall promptly pay any outstanding
          fees.
        </li>
      </UL>
    ),
  },
  {
    id: "data-restrictions",
    heading: "5. Available Data — Uses & Restrictions",
    content: (
      <>
        <P>You will not:</P>
        <UL>
          <li>White-label Available Data</li>
          <li>Include Available Data in blind exchanges</li>
          <li>Resell Available Data beyond what this Agreement permits</li>
          <li>
            Redistribute to third parties except to fulfill a specific client
            campaign
          </li>
          <li>Create derivative data products</li>
          <li>
            Reveal domain-level data to your own clients without permission
          </li>
        </UL>
        <P>
          Available Data may not be used in connection with credit eligibility,
          insurance underwriting, employment screening, credit-repair, or
          unlawful products (including pornography, illegal drugs, or illegal
          weapons).
        </P>
      </>
    ),
  },
  {
    id: "compliance",
    heading: "6. Compliance & Industry Best Practices",
    content: (
      <P>
        Each party will comply with (i) all applicable privacy and
        data-protection laws, (ii) the DAA, NAI, and IAB self-regulatory
        principles, and (iii) all applicable federal and state laws and
        regulations. Marketing communications using Available Data must not
        reference inferred attributes (for example, "Because you are
        pregnant…").
      </P>
    ),
  },
  {
    id: "fees",
    heading: "7. Fees & Payment",
    content: (
      <>
        <P>
          <strong>(a) Pricing.</strong> Applicable fees are set forth in the
          Service Order or the pricing page in effect at the time of
          subscription.
        </P>
        <P>
          <strong>(b) Usage Reporting.</strong> Where applicable, Company will
          provide monthly usage reports within fourteen (14) days following
          month-end.
        </P>
        <P>
          <strong>(c) Payment Terms.</strong> Subscription fees are billed in
          advance per the elected plan (monthly or annual). Invoices for
          usage-based fees are due within seven (7) days of receipt. Past-due
          amounts accrue interest at the lesser of one-half percent (0.5%) per
          month or the maximum lawful rate.
        </P>
        <div className="my-5 rounded-xl border-2 border-[#1e1b4b] bg-[#1e1b4b]/[0.03] p-5">
          <div className="mb-2 text-[11px] font-bold uppercase tracking-[0.18em] text-[#1e1b4b]">
            (d) No Refunds
          </div>
          <p className="text-[15px] font-semibold leading-relaxed text-[#1e1b4b]">
            ALL PAYMENTS ARE FINAL AND NON-REFUNDABLE.
          </p>
          <p className="mt-3 text-slate-700">
            This includes, without limitation, subscription fees (monthly and
            annual), setup fees, usage fees, professional services, training,
            and any other charges. You acknowledge and agree that BuyerBeacon
            Pro begins provisioning Services immediately upon receipt of
            payment, and that no refund, credit, or pro-rated reimbursement
            will be issued for unused time, downgrades, cancellations, or
            termination — whether initiated by you or by BuyerBeacon Pro for
            cause. Cancellation of a subscription will prevent future billing
            but will not refund amounts already paid. By making any payment to
            BuyerBeacon Pro, you expressly waive any right to chargeback or
            dispute payment with your payment provider on the basis of refund
            eligibility.
          </p>
        </div>
        <P>
          <strong>(e) Taxes.</strong> Company is responsible for all applicable
          taxes (sales, use, VAT, GST, withholding, etc.), excluding taxes on
          BuyerBeacon Pro's net income.
        </P>
      </>
    ),
  },
  {
    id: "confidentiality",
    heading: "8. Confidential Information",
    content: (
      <P>
        Neither party will use the other's Confidential Information except as
        permitted by this Agreement, and each party will protect Confidential
        Information using reasonable safeguards no less protective than those
        used for its own confidential information. Breach of confidentiality
        may result in irreparable harm warranting injunctive relief without
        bond.
      </P>
    ),
  },
  {
    id: "security-audit",
    heading: "9. Security Program & Audit",
    content: (
      <P>
        Each party shall maintain an information-security program appropriate
        for the scope and sensitivity of the data handled. BuyerBeacon Pro may
        conduct one (1) audit per twelve (12)-month period upon five (5)
        business days' notice.
      </P>
    ),
  },
  {
    id: "responsible-data",
    heading: "10. Responsible Data Handling",
    content: (
      <P>
        Both parties shall comply with all applicable rules and laws including
        the FTC Act, CCPA/CPRA, GDPR (where applicable), CAN-SPAM, TCPA, and
        the self-regulatory principles of the IAB, DAA, NAI, and EDAA. Company
        represents and warrants that it has obtained any necessary consents
        from end users.
      </P>
    ),
  },
  {
    id: "warranties",
    heading: "11. Warranties & Disclaimers",
    content: (
      <>
        <P>
          Company warrants it has the capacity, software, and personnel
          necessary to perform its obligations. Each party warrants it is duly
          formed, authorized, and not in violation of any other agreement.
        </P>
        <P className="font-semibold uppercase tracking-wide">
          Except as expressly stated, BuyerBeacon Pro makes no warranties,
          express or implied, including implied warranties of merchantability,
          fitness for a particular purpose, accuracy, currency, or
          completeness.
        </P>
      </>
    ),
  },
  {
    id: "liability",
    heading: "12. Limitations of Liability",
    content: (
      <>
        <P className="font-semibold uppercase tracking-wide">
          Neither party shall be liable for any indirect, incidental, special,
          or consequential damages (including loss of income, revenue, profits,
          or goodwill), even if advised of the possibility of such damages.
        </P>
        <P className="font-semibold uppercase tracking-wide">
          BuyerBeacon Pro's aggregate liability under this Agreement shall not
          exceed two times (2x) the average monthly fees paid by Company in the
          trailing twelve (12) months.
        </P>
        <P>
          For damages arising from unauthorized disclosure of Confidential
          Information, the aggregate cap shall not exceed the greater of (a)
          US $250,000 or (b) six times (6x) the average monthly fees paid
          under this Agreement. These limitations do not apply to
          indemnification obligations or to damages resulting from gross
          negligence or intentional misconduct.
        </P>
      </>
    ),
  },
  {
    id: "indemnification",
    heading: "13. Indemnification",
    content: (
      <P>
        Each party shall indemnify the other for third-party claims arising
        from (i) the indemnifying party's violation of law, (ii) misuse of the
        other party's Confidential Information, or (iii) breach of this
        Agreement. Company shall further indemnify BuyerBeacon Pro for any
        misuse or misappropriation of Available Data.
      </P>
    ),
  },
  {
    id: "general",
    heading: "14. General Provisions",
    content: (
      <UL>
        <li>
          <strong>Assignment.</strong> Company may not assign this Agreement
          without our prior written consent, except in connection with a
          merger or change of control.
        </li>
        <li>
          <strong>Independent Contractors.</strong> The parties are independent
          contractors; no agency, partnership, or joint venture is created.
        </li>
        <li>
          <strong>Force Majeure.</strong> Neither party is liable for failure
          caused by events beyond reasonable control.
        </li>
        <li>
          <strong>Notices.</strong> Notices to BuyerBeacon Pro must be sent to
          32 N Gould St., Ste R, Sheridan, WY 82801, with email copy to
          admin@buyerbeacon.pro.
        </li>
        <li>
          <strong>Governing Law / Venue.</strong> This Agreement is governed by
          the laws of the State of Wyoming, without regard to its
          conflict-of-laws rules. The parties submit to the exclusive
          jurisdiction of the state and federal courts located in Sheridan
          County, Wyoming.
        </li>
        <li>
          <strong>Severability.</strong> If any provision is held
          unenforceable, the remainder of the Agreement remains in effect.
        </li>
        <li>
          <strong>Entire Agreement.</strong> This Agreement is the entire
          understanding between the parties and supersedes prior agreements on
          the subject matter.
        </li>
      </UL>
    ),
  },
];

export default function TermsPage() {
  return (
    <LegalLayout
      eyebrow="Legal"
      title="Terms of Service"
      lastUpdated="February 2026"
      path="/terms"
      seoDescription="BuyerBeacon Pro Terms of Service — Master Service Agreement, payment terms (no refunds), Wyoming governing law, and customer obligations."
      sections={sections}
    />
  );
}
